// App.js
import React, { useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { Amplify } from 'aws-amplify';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { createTheme, defaultTheme } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import CancelButton from './components/CancelButton';
import './custom-styles.css';
import './styles/quantumblack-theme.css';
import './styles/pricing-form.css';
import './styles/pricing-results.css';
import './styles/consultation-form.css';
import './styles/modal.css';
import foxLogo from './assets/Fox-River-AI-Logo.png';
import HomePage from './pages/HomePage';
import PublicHomePage from './pages/PublicHomePage';
import BlogPage from './pages/BlogPage';
import ChatBedrock from './pages/ChatBedrock';
import DataPreparation from './pages/DataPreparation';
import ConversationalAgents from './pages/ConversationalAgents';
import PredictiveAnalytics from './pages/PredictiveAnalytics';
import PrescriptiveAI from './pages/PrescriptiveAI';
import RecommendationSystems from './pages/RecommendationSystems';
import ComputerVision from './pages/ComputerVision';
import NLP from './pages/NLP';
import CustomAISolutions from './pages/CustomAISolutions';
import PricingCalculator from './pages/PricingCalculator';
import ContactUs from './pages/ContactUs';
import InventoryOptimization from './pages/InventoryOptimization';
import AccountManagement from './pages/AccountManagement';
import DatabricksUSPS from './pages/DatabricksUSPS';
import AdminBlogPage from './pages/AdminBlogPage';

// Import outputs conditionally - will be generated after backend deployment
let outputs = {};
try {
  outputs = require('./amplify_outputs.json');
} catch (error) {
  console.log('amplify_outputs.json not found - backend not deployed yet');
}

// Import test utility for development
if (process.env.NODE_ENV === 'development') {
  import('./utils/stripe-test-session');
}

// Simplified Amplify config
try {
  Amplify.configure(outputs);
  console.log('Amplify Gen 2 configured successfully');
} catch (error) {
  console.error('Amplify configuration failed:', error);
}

const myDarkTheme = createTheme({
  ...defaultTheme,
  name: 'relu-dark-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          80: '#FF9900',
        },
      },
      background: {
        primary: '#121212',
      },
      font: {
        primary: {
          value: 'Arial',
        },
      },
    },
  },
});

const AppContent = ({ signOut, user }) => {
  return <AdminBlogPage signOut={signOut} user={user} />;
};

// Protected routes component
const ProtectedApp = () => {
  const logoRef = useRef(null);
  
  return (
    <ThemeProvider theme={myDarkTheme}>
      <Authenticator
        hideSignUp={true}
        loginMechanisms={['email']}
        components={{
          Header() {
            return (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#1E1E1E', padding: '10px' }}>
                <div 
                  onClick={() => window.location.href = '/'}
                  style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
                >
                  <img
                    src={foxLogo}
                    alt="Fox Logo"
                    id="foxLogo"
                    style={{ width: '50px', marginRight: '10px' }}
                    ref={logoRef}
                  />
                  <div>
                    <h1 style={{ color: '#FF9900', margin: 0, textAlign: 'center' }}>Fox River AI Admin</h1>
                    <p style={{ color: '#FFFFFF', margin: 0, textAlign: 'center' }}>Blog Management Access</p>
                  </div>
                </div>
              </div>
            );
          },
          Footer() {
            return (
              <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1E1E1E' }}>
                <CancelButton />
              </div>
            );
          },
        }}
      >
        {({ signOut, user }) => (
          <AppContent signOut={signOut} user={user} />
        )}
      </Authenticator>
    </ThemeProvider>
  );
};

function App() {
  console.log('App component rendering...');

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public homepage - no authentication required */}
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postId" element={<BlogPage />} />
        
        {/* Public app routes - no authentication required */}
        <Route path="/app" element={<HomePage />} />
        <Route path="/chat-bedrock" element={<ChatBedrock />} />
        <Route path="/data-preparation" element={<DataPreparation />} />
        <Route path="/conversational-agents" element={<ConversationalAgents />} />
        <Route path="/predictive-analytics" element={<PredictiveAnalytics />} />
        <Route path="/prescriptive-ai" element={<PrescriptiveAI />} />
        <Route path="/recommendation-systems" element={<RecommendationSystems />} />
        <Route path="/computer-vision" element={<ComputerVision />} />
        <Route path="/nlp" element={<NLP />} />
        <Route path="/custom-ai-solutions" element={<CustomAISolutions />} />
        <Route path="/pricing-calculator" element={<PricingCalculator skipAuth={true} />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/inventory-optimization" element={<InventoryOptimization />} />
        <Route path="/account" element={<AccountManagement />} />
        <Route path="/databricks" element={<DatabricksUSPS />} />
        
        {/* Protected admin route - authentication required */}
        <Route path="/admin/blog" element={<ProtectedApp />} />
      </Routes>
    </Router>
  );
}

export default App;